import paginationService from "../../../../../common/utils/pagination/pagination.service";
import BillingsTaxiUnbilledRouteListingFilterType from "./types/billings-taxi-unbilled-route-listing-filter-type";
import BillingsTaxiUnbilledRouteListingSortKey from "./types/billings-taxi-unbilled-route-listing-sort-key";
import BillingsTaxiUnbilledRouteListingFilter, {
  BillingsTaxiUnbilledRouteListingClientFilter,
  BillingsTaxiUnbilledRouteListingDriverFilter,
  BillingsTaxiUnbilledRouteListingFleetPartnerFilter,
  BillingsTaxiUnbilledRouteListingOrderInternalIdFilter,
  BillingsTaxiUnbilledRouteListingRouteAddressFilter,
  BillingsTaxiUnbilledRouteListingRouteDestinationAddressFilter,
  BillingsTaxiUnbilledRouteListingRouteIdFilter,
  BillingsTaxiUnbilledRouteListingRouteIntermediateAddressFilter,
  BillingsTaxiUnbilledRouteListingRoutePickupAddressFilter,
  BillingsTaxiUnbilledRouteListingRouteStartDateFilter,
} from "./types/billings-taxi-unbilled-route-listing-filter";
import TaxiCorporationUnbilledRouteListParams, {
  TaxiCorporationUnbilledRouteListParamsOrder,
} from "../../../../../common/services/taxi-corporation/unbilled/route-list/taxi-corporation-unbilled-route-list-params";

const createRequestOrder = (
  sortKey: BillingsTaxiUnbilledRouteListingSortKey | null,
): TaxiCorporationUnbilledRouteListParamsOrder | undefined => {
  const options: {
    sortKey: BillingsTaxiUnbilledRouteListingSortKey;
    requestOrder: TaxiCorporationUnbilledRouteListParamsOrder;
  }[] = [
    {
      requestOrder: TaxiCorporationUnbilledRouteListParamsOrder.START_TIME_DESC,
      sortKey: BillingsTaxiUnbilledRouteListingSortKey.START_TIME_DESC,
    },
    {
      requestOrder: TaxiCorporationUnbilledRouteListParamsOrder.START_TIME_ASC,
      sortKey: BillingsTaxiUnbilledRouteListingSortKey.START_TIME_ASC,
    },
    {
      requestOrder: TaxiCorporationUnbilledRouteListParamsOrder.HUMAN_ID_ASC,
      sortKey: BillingsTaxiUnbilledRouteListingSortKey.ROUTE_ID_ASC,
    },
    {
      requestOrder: TaxiCorporationUnbilledRouteListParamsOrder.HUMAN_ID_DESC,
      sortKey: BillingsTaxiUnbilledRouteListingSortKey.ROUTE_ID_DESC,
    },
    {
      requestOrder: TaxiCorporationUnbilledRouteListParamsOrder.DRIVER_ASC,
      sortKey: BillingsTaxiUnbilledRouteListingSortKey.DRIVER_ASC,
    },
    {
      requestOrder: TaxiCorporationUnbilledRouteListParamsOrder.DRIVER_DESC,
      sortKey: BillingsTaxiUnbilledRouteListingSortKey.DRIVER_DESC,
    },
  ];

  return options.find((option) => option.sortKey === sortKey)?.requestOrder;
};

const createRequest = (
  taxiCorporationUuid: string,
  page: number,
  pageSize: number,
  filters: BillingsTaxiUnbilledRouteListingFilter[],
  sortKey: BillingsTaxiUnbilledRouteListingSortKey | null,
): TaxiCorporationUnbilledRouteListParams => {
  const offset = paginationService.calculateOffset(page, pageSize);

  return {
    taxiCorporationUuid: taxiCorporationUuid,
    limit: pageSize,
    offset,
    order: createRequestOrder(sortKey),
    humanId: (
      filters.find(
        (filter) =>
          filter.type === BillingsTaxiUnbilledRouteListingFilterType.ROUTE_ID,
      )?.value as
        | BillingsTaxiUnbilledRouteListingRouteIdFilter["value"]
        | undefined
    )?.toString(),
    driver: filters.find(
      (filter) =>
        filter.type === BillingsTaxiUnbilledRouteListingFilterType.DRIVER,
    )?.value as
      | BillingsTaxiUnbilledRouteListingDriverFilter["value"]
      | undefined,
    startTimeSince: (
      filters.find(
        (filter) =>
          filter.type ===
          BillingsTaxiUnbilledRouteListingFilterType.ORDER_START_DATE,
      )?.value as
        | BillingsTaxiUnbilledRouteListingRouteStartDateFilter["value"]
        | undefined
    )?.from.toJSON(),
    startTimeTo: (
      filters.find(
        (filter) =>
          filter.type ===
          BillingsTaxiUnbilledRouteListingFilterType.ORDER_START_DATE,
      )?.value as
        | BillingsTaxiUnbilledRouteListingRouteStartDateFilter["value"]
        | undefined
    )?.to.toJSON(),
    client: filters.find(
      (filter) =>
        filter.type === BillingsTaxiUnbilledRouteListingFilterType.CLIENT,
    )?.value as
      | BillingsTaxiUnbilledRouteListingClientFilter["value"]
      | undefined,

    externalId: filters.find(
      (filter) =>
        filter.type ===
        BillingsTaxiUnbilledRouteListingFilterType.ORDER_INTERNAL_ID,
    )?.value as
      | BillingsTaxiUnbilledRouteListingRoutePickupAddressFilter["value"]
      | undefined,
    startAddress: filters.find(
      (filter) =>
        filter.type ===
        BillingsTaxiUnbilledRouteListingFilterType.ROUTE_PICKUP_ADDRESS,
    )?.value as
      | BillingsTaxiUnbilledRouteListingRoutePickupAddressFilter["value"]
      | undefined,
    midAddress: filters.find(
      (filter) =>
        filter.type ===
        BillingsTaxiUnbilledRouteListingFilterType.ROUTE_INTERMEDIATE_ADDRESS,
    )?.value as
      | BillingsTaxiUnbilledRouteListingRouteIntermediateAddressFilter["value"]
      | undefined,
    endAddress: filters.find(
      (filter) =>
        filter.type ===
        BillingsTaxiUnbilledRouteListingFilterType.ROUTE_DESTINATION_ADDRESS,
    )?.value as
      | BillingsTaxiUnbilledRouteListingRouteDestinationAddressFilter["value"]
      | undefined,
    address: filters.find(
      (filter) =>
        filter.type ===
        BillingsTaxiUnbilledRouteListingFilterType.ROUTE_ADDRESS,
    )?.value as
      | BillingsTaxiUnbilledRouteListingRouteAddressFilter["value"]
      | undefined,
    cargoOrderHumanId: filters.find(
      (filter) =>
        filter.type ===
        BillingsTaxiUnbilledRouteListingFilterType.ORDER_INTERNAL_ID,
    )?.value as
      | BillingsTaxiUnbilledRouteListingOrderInternalIdFilter["value"]
      | undefined,
    fleetPartner: filters.find(
      (filter) =>
        filter.type ===
        BillingsTaxiUnbilledRouteListingFilterType.FLEET_PARTNER,
    )?.value as
      | BillingsTaxiUnbilledRouteListingFleetPartnerFilter["value"]
      | undefined,
  };
};
const billingsTaxiUnbilledRouteListingRequestFactory = {
  createRequest,
};

export default billingsTaxiUnbilledRouteListingRequestFactory;
